import { Button, Input, Loader, Select } from '@trackman/web-shared-components';
import Switch from 'components/Switch/Switch';
import { UmpireSearch } from 'components/UmpireSearch/UmpireSearch';
import { ReactComponent as CloseIcon } from 'components/icons/closeIcon.svg';
import { ReactComponent as LockIcon } from 'components/icons/lockIcon.svg';
import { SelectWrapper } from 'components/ui/SelectWrapper/SelectWrapper';
import { ServiceTypes } from 'constants/ServiceTypes';
import dayjs from 'dayjs';
import { IOwningOrganization } from 'interfaces/session/IOwningOrganization';
import { IApiSessionTeam, IField, ILeague, ILevel, ISession, IVenue } from 'interfaces/session/ISession';
import { ISessionUpdateRequest } from 'interfaces/session/ISessionUpdateRequest';
import { ISearchTeam } from 'interfaces/team/ISearchTeam';
import { IUmpire } from 'interfaces/umpire/IUmpire';
import moment from 'moment';
import { TeamSelector } from 'pages/Sessions/Components/TeamSelector';
import {
  TeamsLockInfo,
  TeamsLockText,
  TeamsLockWrapper,
} from 'pages/Verify/Components/TopBar/EditSessionModal/components/TeamsAndSettings/TeamsAndSettingsStyles';
import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { notify } from 'services/NotificationService';
import { SessionVerificationApiService } from 'services/data-services/SessionVerificationService';
import { useInjection } from 'services/ioc/ioc.react';
import { SessionPlayStore } from 'stores/SessionPlayStore';
import { SessionVerificationStore } from 'stores/SessionVerificationStore';
import { TeamStore } from 'stores/TeamStore';
import {
  ButtonWrapper,
  EditInputsContainer,
  EditTeams,
  EditWrapper,
  LoaderWrapper,
  SessionSettingsWrapper,
  SmallTitle,
  StyledButton,
  StyledClearIcon,
  StyledSwitchIcon,
  TeamsContainer,
  TeamSelectContainer,
  TeamSelectorWrapper,
  TeamWrapper,
  Wrapper,
} from './SessionSettingsStyles';

export const SessionSettings: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const history = useHistory();
  const teamStore = useInjection<TeamStore>(ServiceTypes.TeamStore);
  const sessionStore = useInjection<SessionVerificationStore>(ServiceTypes.SessionVerificationStore);
  const [isEditTeamsLocked, setIsEditTeamsLocked] = useState<boolean>(true);
  const [selectedLeague, setSelectedLeague] = useState<ILeague>({} as ILeague);
  const [selectedLevel, setSelectedLevel] = useState<ILevel>({} as ILevel);
  const [selectedField, setSelectedField] = useState<IField>({} as IField);
  const [selectedVenue, setSelectedVenue] = useState<IVenue>({} as IVenue);
  const [dailySequenceNumber, setDailySequenceNumber] = useState<number | undefined>(undefined);
  const [externalSessionId, setExternalSessionId] = useState<string>();
  const [externalEventId, setExternalEventId] = useState<string>();
  const [selectedOrg, setSelectedOrg] = useState<IOwningOrganization>();
  const [owningOrganizations, setOwningOrganizations] = useState<IOwningOrganization[]>([]);
  const [selectedHomeTeam, setSelectedHomeTeam] = useState<ISearchTeam>();
  const [selectedAwayTeam, setSelectedAwayTeam] = useState<ISearchTeam>();
  const [invalidExternalId, setInvalidExternalId] = useState(false);
  const sessionPlayStore: SessionPlayStore = useInjection<SessionPlayStore>(ServiceTypes.SessionPlayStore);
  const emptyOrganization = { id: '', name: '---', shortName: '' };
  const [selectedUmpire, setSelectedUmpire] = useState<IUmpire>();
  const sessionService = useInjection<SessionVerificationApiService>(ServiceTypes.SessionVerificationApiService);
  const sessionVerifierStore = useInjection<SessionVerificationStore>(ServiceTypes.SessionVerificationStore);
  const [session, setSession] = useState<ISession | null>(null);
  const [homeTeamInputValue, setHomeTeamInputValue] = useState<string>('');
  const [awayTeamInputValue, setAwayTeamInputValue] = useState<string>('');
  const [updatingSession, setUpdatingSession] = useState(false);

  const fetchOwningOrganizations = async () => {
    if (!selectedField.id) {
      setOwningOrganizations([emptyOrganization]);
      return;
    }

    const owningOrgs = await sessionStore.fetchOwningOrganizations(selectedField.id);
    setOwningOrganizations([emptyOrganization, ...owningOrgs]);
  };

  const getSession = async () => {
    await sessionVerifierStore.getContextualData();

    const sessionTask = sessionService.getSessionById(sessionId);
    const [session] = await Promise.all([sessionTask]);
    if (sessionStore.verifySessionProperties(session)) {
      sessionPlayStore.loadRosters(session.homeTeam?.id, session.awayTeam?.id);
    }

    if (session.isLocked) {
      history.push(`/search/teamId/undefined/date/${dayjs(session.utcStartTime).format('YYYY-MM-DD')}?locked=true`);
    }

    setSession(session);
  };

  const contextualDataToDropdownMap = (obj: ILeague | ILevel | IOwningOrganization | IField | IVenue) => ({
    label: obj.name,
    value: obj.id,
  });

  const unlockTeamEdit = async () => {
    if (updatingSession) {
      return;
    }

    if (teamStore.teams.length === 0) {
      await teamStore.fetchTeams();
    }
    setIsEditTeamsLocked((prevState) => !prevState);
  };

  const switchTeams = () => {
    const awayTeamCpy = selectedAwayTeam ? { ...selectedAwayTeam } : undefined;
    setSelectedAwayTeam(selectedHomeTeam);
    setSelectedHomeTeam(awayTeamCpy);
  };

  const getHomeTeam = () => {
    if (session && session.homeTeam?.id === selectedHomeTeam!.id) {
      return session.homeTeam;
    }
    return {
      id: selectedHomeTeam!.id,
      name: selectedHomeTeam!.name,
      shortName: selectedHomeTeam!.shortName,
      externalId: undefined,
      lineup: [],
    } as IApiSessionTeam;
  };

  const getAwayTeam = () => {
    if (session && session.awayTeam?.id === selectedAwayTeam!.id) {
      return session.awayTeam;
    }
    return {
      id: selectedAwayTeam!.id,
      name: selectedAwayTeam!.name,
      shortName: selectedAwayTeam!.shortName,
      externalId: undefined,
      lineup: [],
    } as IApiSessionTeam;
  };

  const useNewSettingsHandler = async () => {
    if (session && externalSessionId && externalSessionId !== session.externalSessionId) {
      const isExternalIdValid = await sessionStore.validateExternalSessionId(externalSessionId);

      if (!isExternalIdValid) {
        setInvalidExternalId(true);
        return;
      }
    }

    if (!selectedAwayTeam || !selectedHomeTeam || !session) {
      return;
    }

    const updateRequest = {
      id: session.id,
      field: { ...selectedField },
      venue: { ...selectedVenue },
      owningOrganizationId: selectedOrg && selectedOrg.id !== '' ? selectedOrg.id : undefined,
      league: { ...selectedLeague },
      level: { ...selectedLevel },
      isVerified: false,
      isPlayerTrackingVerified: false,
      homeTeam: getHomeTeam(),
      awayTeam: getAwayTeam(),
      externalSessionId,
      dailySequenceNumber,
      umpire: {
        ...selectedUmpire,
        isPlaceholder: selectedUmpire?.isPlaceholder ? true : false,
      },
    } as ISessionUpdateRequest;

    updateSession(updateRequest);
  };

  const updateSession = async (update: ISessionUpdateRequest) => {
    if (!session) {
      return;
    }

    setUpdatingSession(true);
    const result = await sessionPlayStore.updateSession({ ...session, ...update }, []).then((result) => {
      setUpdatingSession(false);
      return result;
    });
    if (result) {
      notify('Success', 'Session updated successfully', 'success');
    }
  };

  const exitSessionHandler = () => {
    if (session) {
      const date = moment.parseZone(session.localStartTime).format('YYYY-MM-DD');

      history.push(`/search/teamId/undefined/date/${date}`);
    }
  };

  useEffect(() => {
    getSession();
  }, []);

  useEffect(() => {
    fetchOwningOrganizations();
    setSelectedOrg(emptyOrganization);
  }, [selectedField]);

  useEffect(() => {
    if (!isEditTeamsLocked && !teamStore.isLoading && teamStore.teams.length === 0) {
      teamStore.fetchTeams();
    }
  }, [isEditTeamsLocked]);

  useEffect(() => {
    if (
      session &&
      owningOrganizations.length > 0 &&
      session.owningOrganizationId &&
      owningOrganizations.some((org) => org.id === session.owningOrganizationId)
    ) {
      setSelectedOrg(owningOrganizations.find((x) => x.id === session.owningOrganizationId));
    }
  }, [session, owningOrganizations]);

  useEffect(() => {
    if (session) {
      setSelectedLeague(session.league);
      setExternalSessionId(session.externalSessionId);
      setExternalEventId(session.externalEventId);
      setSelectedLevel(session.level);
      setSelectedField(sessionStore.fields.find((x) => x.id === session.field.id) || ({} as IField));
      setSelectedVenue(sessionStore.venues.find((x) => x.id === session.venue.id) || ({} as IVenue));
      setSelectedHomeTeam(session.homeTeam && { ...session.homeTeam });
      setSelectedAwayTeam(session.awayTeam && { ...session.awayTeam });
      setDailySequenceNumber(session.dailySequenceNumber || undefined);
      setSelectedUmpire(sessionStore.umpires.find((umpire) => umpire.id === session.umpire?.id));
    }
  }, [session, sessionStore.fields, sessionStore.venues]);

  return !session ? (
    <LoaderWrapper>
      <Loader size='large' />
    </LoaderWrapper>
  ) : (
    <Wrapper>
      <EditWrapper>
        <ButtonWrapper>
          <StyledButton
            label=''
            startIcon={<CloseIcon />}
            intent='primary'
            onClick={() => exitSessionHandler()}
            width={'32px'}
            style={{ minWidth: '32px' }}
            data-testid='close-session-settings-button'
          />
          <div>Session Settings</div>
        </ButtonWrapper>
        <Button
          intent='primary'
          label='Save'
          disabled={!selectedAwayTeam || !selectedHomeTeam || !selectedLeague || !selectedLevel || updatingSession}
          onClick={useNewSettingsHandler}
          isLoading={updatingSession}
          data-testid='save-session-settings-button'
        />
      </EditWrapper>
      <SessionSettingsWrapper>
        <TeamsContainer>
          <SmallTitle margin='0 0 7px 0'>
            <div>Teams</div>
          </SmallTitle>
          <EditTeams>
            <TeamsLockWrapper>
              <TeamsLockInfo>
                <LockIcon />
                <TeamsLockText>
                  <div>Team Settings Locked</div>
                  <div>Enable to modify team information and swap home/away teams</div>
                </TeamsLockText>
              </TeamsLockInfo>
              <Switch
                checked={!isEditTeamsLocked}
                reversed
                onClick={unlockTeamEdit}
                disabled={updatingSession}
                data-testid='team-settings-switch'
              />
            </TeamsLockWrapper>
          </EditTeams>
          <TeamSelectContainer>
            <TeamWrapper>
              <TeamSelectorWrapper disabled={isEditTeamsLocked}>
                <StyledClearIcon
                  onClick={() => {
                    if (!isEditTeamsLocked && !updatingSession) {
                      setSelectedAwayTeam(undefined);
                      setAwayTeamInputValue('');
                    }
                  }}
                  data-testid='clear-away-team-icon'
                />
                <TeamSelector
                  label='Away Team'
                  onSelectionChange={(selectedTeam) => setSelectedAwayTeam(selectedTeam)}
                  isDisabled={teamStore.isLoading || isEditTeamsLocked || updatingSession}
                  selectedTeam={selectedAwayTeam}
                  inputValue={awayTeamInputValue}
                  setInputValue={setAwayTeamInputValue}
                  isError={!selectedAwayTeam}
                  data-testid='away-team-selector'
                />
              </TeamSelectorWrapper>
              <Input
                label='External event team ID'
                type='text'
                value={session.awayTeam.externalId}
                onChange={() => {}}
                disabled
                width={'312px'}
                data-testid='away-team-external-id-input'
              />
            </TeamWrapper>
            <StyledSwitchIcon onClick={() => !isEditTeamsLocked && switchTeams()} disabled={teamStore.isLoading || isEditTeamsLocked} />
            <TeamWrapper>
              <TeamSelectorWrapper disabled={isEditTeamsLocked}>
                <StyledClearIcon
                  onClick={() => {
                    if (!isEditTeamsLocked && !updatingSession) {
                      setSelectedHomeTeam(undefined);
                      setHomeTeamInputValue('');
                    }
                  }}
                  data-testid='clear-home-team-icon'
                />{' '}
                <TeamSelector
                  label='Home Team'
                  onSelectionChange={(selectedTeam) => {
                    setSelectedHomeTeam(selectedTeam);
                  }}
                  isDisabled={teamStore.isLoading || isEditTeamsLocked || updatingSession}
                  selectedTeam={selectedHomeTeam}
                  inputValue={homeTeamInputValue}
                  setInputValue={setHomeTeamInputValue}
                  isError={!selectedHomeTeam}
                  data-testid='home-team-selector'
                />
              </TeamSelectorWrapper>
              <Input
                label='External event team ID'
                type='text'
                value={session.homeTeam.externalId}
                onChange={() => {}}
                disabled
                width={'312px'}
                data-testid='home-team-external-id-input'
              />
            </TeamWrapper>
          </TeamSelectContainer>
          <SmallTitle margin='30px 0 16px 0'>Settings</SmallTitle>
          <EditInputsContainer>
            <SelectWrapper>
              <Select
                label='League'
                selectOptions={sessionStore.leagues.map(contextualDataToDropdownMap)}
                value={(selectedLeague && contextualDataToDropdownMap(selectedLeague).value) || ''}
                onChange={(event: any) => {
                  setSelectedLeague(sessionStore.leagues.find((x) => x.id === event.target.value)!);
                }}
                width='324px'
                required
                error={!selectedLeague}
                disabled={updatingSession}
                data-testid='league-select'
              />
            </SelectWrapper>
            <SelectWrapper>
              <Select
                label='Level'
                selectOptions={sessionStore.levels.map(contextualDataToDropdownMap)}
                value={(selectedLevel && contextualDataToDropdownMap(selectedLevel).value) || ''}
                onChange={(event: any) => {
                  setSelectedLevel(sessionStore.levels.find((x) => x.id === event.target.value)!);
                }}
                width='324px'
                required
                error={!selectedLevel}
                disabled={updatingSession}
                data-testid='level-select'
              />
            </SelectWrapper>
          </EditInputsContainer>
          <EditInputsContainer>
            <SelectWrapper>
              <Select
                label='Owning organization'
                selectOptions={owningOrganizations.map(contextualDataToDropdownMap)}
                value={(selectedOrg && contextualDataToDropdownMap(selectedOrg).value) || ''}
                onChange={(event: any) => {
                  setSelectedOrg(owningOrganizations.find((x) => x.id === event.target.value)!);
                }}
                width='324px'
                disabled={updatingSession}
                data-testid='owning-organization-select'
              />
            </SelectWrapper>
            <UmpireSearch
              selectedUmpire={selectedUmpire}
              setSelectedUmpire={setSelectedUmpire}
              width={'324px'}
              margin={0}
              disabled={updatingSession}
            />
          </EditInputsContainer>
          <EditInputsContainer>
            <SelectWrapper>
              <Select
                label='Venue'
                selectOptions={sessionStore.venues.map(contextualDataToDropdownMap)}
                value={(selectedVenue && contextualDataToDropdownMap(selectedVenue).value) || ''}
                onChange={(event: any) => {
                  setSelectedVenue(sessionStore.venues.find((x) => x.id === event.target.value)!);
                  setSelectedField({} as IField);
                }}
                width='324px'
                disabled={updatingSession}
                data-testid='venue-select'
              />
            </SelectWrapper>
            <SelectWrapper>
              <Select
                label='Field'
                selectOptions={sessionStore.fields
                  .filter((f) => selectedVenue.fields && selectedVenue.fields.some((id) => f.id === id))
                  .map(contextualDataToDropdownMap)}
                value={(selectedField && contextualDataToDropdownMap(selectedField).value) || ''}
                onChange={(event: any) => {
                  setSelectedField(sessionStore.fields.find((x) => x.id === event.target.value)!);
                }}
                width='324px'
                disabled={updatingSession}
                data-testid='field-select'
              />
            </SelectWrapper>
          </EditInputsContainer>
          <SmallTitle margin='30px 0 16px 0'>Game reference settings</SmallTitle>
          <EditInputsContainer>
            <Input
              label='Game reference'
              type='text'
              value={session.gameReference || ''}
              onChange={() => {}}
              disabled
              width={'324px'}
              InputProps={{
                // @ts-ignore
                'data-testid': 'game-reference-input',
              }}
            />
            <Input
              label='Sequence number'
              type='number'
              value={dailySequenceNumber || ''}
              onChange={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                if (parseInt(event.target.value) > 0) {
                  setDailySequenceNumber(parseInt(event.target.value));
                }
              }}
              width={'324px'}
              disabled={updatingSession}
              InputProps={{
                // @ts-ignore
                'data-testid': 'sequence-number-input',
              }}
            />
          </EditInputsContainer>
          <EditInputsContainer>
            <Input
              label='External ID'
              type='text'
              value={externalSessionId || ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                setExternalSessionId(e.target.value);
                setInvalidExternalId(false);
              }}
              width={'324px'}
              required
              error={invalidExternalId}
              disabled={updatingSession}
              InputProps={{
                // @ts-ignore
                'data-testid': 'external-id-input',
              }}
            />
            <Input
              label='Event ID'
              type='text'
              value={externalEventId || ''}
              onChange={() => {}}
              width={'324px'}
              disabled
              InputProps={{
                // @ts-ignore
                'data-testid': 'event-id-input',
              }}
            />
          </EditInputsContainer>
        </TeamsContainer>
      </SessionSettingsWrapper>
    </Wrapper>
  );
};
